import { Autocomplete, CircularProgress, TextField, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { debounce } from 'lodash-es';
import {
  ISourcingTypeaheadResult,
  searchSourcingCompanies,
} from '../../../services/queries/SourcingCompanyQueries';
import { MUIAvatar } from '../../../components/Avatar/MUIAvatar';

const DEBOUNCE_MS = 500;

interface SourcingEntitySearchProps {
  readonly onSelect: (result: ISourcingTypeaheadResult) => void;
}

export function SourcingEntitySearch({ onSelect }: SourcingEntitySearchProps) {
  const [options, setOptions] = useState<ISourcingTypeaheadResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const debouncedSearch = useMemo(
    () =>
      debounce(async (text: string) => {
        setIsSearching(true);
        try {
          const results = await searchSourcingCompanies(text, 10, true);
          setOptions(results);
        } finally {
          setIsSearching(false);
        }
      }, DEBOUNCE_MS),
    []
  );

  const handleInputChange = useCallback(
    (_event: unknown, value: string, reason: string) => {
      if (reason === 'input' || reason === 'clear') {
        debouncedSearch.cancel();
        if (value === '') {
          setOptions([]);
        } else {
          debouncedSearch(value);
        }
      }
    },
    [debouncedSearch]
  );

  const handleChange = useCallback(
    (_event: unknown, value: ISourcingTypeaheadResult | null) => {
      if (value) {
        onSelect(value);
      }
    },
    [onSelect]
  );

  return (
    <Autocomplete
      autoHighlight
      blurOnSelect
      disablePortal
      openOnFocus
      size='small'
      filterOptions={(opts) => opts}
      noOptionsText={
        <span style={{ fontSize: '0.8125rem' }}>{isSearching ? 'Searching...' : 'Type to search'}</span>
      }
      popupIcon={null}
      options={options}
      getOptionLabel={(opt) => opt.name}
      isOptionEqualToValue={(opt, val) => opt.id === val.id}
      onInputChange={handleInputChange}
      onChange={handleChange}
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus
          placeholder='Search for a company...'
          sx={{
            minWidth: 360,
            '& .MuiInputBase-root': { height: 28, py: 0 },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: isSearching ? <CircularProgress size={16} /> : params.InputProps.endAdornment,
          }}
        />
      )}
      renderOption={(props, option) => (
        <li
          {...props}
          key={option.id}
          style={{
            ...props.style,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '4px 8px',
          }}
        >
          <MUIAvatar nameParts={option.name.split(' ')} src={option.logo ?? null} size='small' />
          <Typography variant='body2' noWrap sx={{ flex: 1, minWidth: 0 }}>
            {option.name}
          </Typography>
          {option.domain && (
            <Typography
              variant='caption'
              color='text.secondary'
              noWrap
              sx={{ flexShrink: 0, maxWidth: '40%' }}
            >
              {option.domain}
            </Typography>
          )}
        </li>
      )}
    />
  );
}
